import { measureElement, Virtualizer } from '@tanstack/react-virtual';

export type OptionVirtualizer = Virtualizer<HTMLDivElement, Element>;

// Height estimates for rows that have never been rendered (measured rows
// always win — see measurePositionedElement). A wrong estimate is corrected on
// the row's first mount, which shifts the content under an in-flight scroll
// gesture, so the estimate should match the real height of the common case:
// 40 = 10px paddings + one 20px line box; a helpText row adds 2px padding each
// side and a second line box. Content that wraps beyond one line per block
// still corrects on first measurement — line count isn't knowable pre-layout.
const OPTION_ESTIMATE_HEIGHT = 40;
const HELP_OPTION_ESTIMATE_HEIGHT = 64;

export const estimateOptionHeight = (option: { helpText?: string | null } | undefined) =>
  option?.helpText ? HELP_OPTION_ESTIMATE_HEIGHT : OPTION_ESTIMATE_HEIGHT;

// base-ui lays the popup out invisibly before floating-ui has positioned it,
// and `--anchor-width` is only set once positioning lands — until then the
// Positioner's width rule collapses to nothing and option labels wrap
// per-character. Recording those degenerate heights poisons the offsets of any
// row that unmounts before the corrective ResizeObserver measurement fires
// (the list then jumps as those rows re-measure on scroll), so report the
// cached/estimated size until the popup is actually positioned.
export const measurePositionedElement = (
  element: Element,
  entry: ResizeObserverEntry | undefined,
  instance: OptionVirtualizer,
): number => {
  if (!getComputedStyle(element).getPropertyValue('--anchor-width')) {
    const index = instance.indexFromElement(element);
    return (
      instance.itemSizeCache.get(instance.options.getItemKey(index)) ??
      instance.options.estimateSize(index)
    );
  }
  return measureElement(element, entry, instance);
};

// scrollToIndex keeps re-aiming at the row for as long as the rows above it are
// still being measured, with no regard for the user scrolling in between; the
// user's first wheel or touch drag hands the list back to them. The hand-back
// waits for the scroll that gesture produces: re-aiming the virtualizer during
// the wheel event itself would scroll to the pre-wheel offset and swallow it.
export const scrollIndexToCenter = (virtualizer: OptionVirtualizer, index: number) => {
  virtualizer.scrollToIndex(index, { align: 'center' });
  const { scrollElement } = virtualizer;
  if (!scrollElement) return;
  const userScroll = new AbortController();
  const stopCenteringOnceScrolled = () => {
    userScroll.abort();
    scrollElement.addEventListener(
      'scroll',
      () => {
        virtualizer.scrollToOffset(scrollElement.scrollTop);
      },
      { passive: true, once: true },
    );
  };
  const listenerOptions = { passive: true, signal: userScroll.signal };
  scrollElement.addEventListener('wheel', stopCenteringOnceScrolled, listenerOptions);
  scrollElement.addEventListener('touchmove', stopCenteringOnceScrolled, listenerOptions);
};

// base-ui scrolls a keyboard-highlighted row into view only while it is
// rendered. A highlight that lands outside the virtualized window (wrapping
// past either edge, or stepping on from a row the user scrolled away from) is
// scrolled to here; the edges keep the rows beside them as context.
export const scrollHighlightIntoView = (virtualizer: OptionVirtualizer, index: number) => {
  const isStart = index === 0;
  const isEnd = index === virtualizer.options.count - 1;
  if (isStart || isEnd) {
    queueMicrotask(() => {
      virtualizer.scrollToIndex(index, { align: isEnd ? 'start' : 'end' });
    });
    return;
  }
  if (virtualizer.getVirtualItems().some((row) => row.index === index)) return;
  queueMicrotask(() => {
    virtualizer.scrollToIndex(index, { align: 'auto' });
  });
};
